import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { saveQuiz } from "@/lib/api";
import { toast } from "sonner";

// Generic multiple-choice quiz. Accepts { id, id_alt, questions:[{id,text,options:[[K,text]|{key,text}],correct}] }
export default function GenericQuizStep({ id, id_alt, title, intro, questions, onDone }) {
  const quizId = id_alt || id;
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const allAnswered = Object.keys(answers).length === questions.length;

  const submit = async () => {
    if (!allAnswered) return;
    setSaving(true);
    try {
      const detailed = questions.map(q => ({
        question_id: q.id,
        selected: answers[q.id],
        correct: answers[q.id] === q.correct,
      }));
      const score = detailed.filter(a => a.correct).length;
      await saveQuiz(quizId, detailed, score);
      toast.success(`Puntuación: ${score}/${questions.length}`);
      onDone({ score });
    } catch {
      toast.error("No pudimos guardar el cuestionario.");
    } finally {
      setSaving(false);
    }
  };

  const normalizeOpts = (opts) => opts.map(o => Array.isArray(o) ? { key: o[0], text: o[1] } : o);

  return (
    <div>
      {intro && <p className="text-[var(--ha-muted)] leading-relaxed max-w-2xl">{intro}</p>}
      <div className="mt-8 space-y-4">
        {questions.map((q, i) => {
          const opts = normalizeOpts(q.options);
          const chosen = answers[q.id];
          return (
            <Card key={q.id} className="rounded-2xl border-[var(--ha-border)] bg-white">
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold">Pregunta {i + 1}</p>
                <p className="mt-2 font-semibold text-[var(--ha-text)] leading-snug">{q.text}</p>
                <div className="mt-4 grid gap-2">
                  {opts.map(o => {
                    const isSel = chosen === o.key;
                    const isCorrect = o.key === q.correct;
                    const feedback = chosen !== undefined;
                    return (
                      <button
                        key={o.key}
                        data-testid={`gq-${quizId}-${q.id}-${o.key}`}
                        onClick={() => setAnswers({ ...answers, [q.id]: o.key })}
                        className={`text-left rounded-xl border px-4 py-3 flex items-start gap-3 transition-colors ${
                          feedback && isSel && isCorrect ? "border-[var(--ha-success)] bg-[var(--ha-success)]/10" :
                          feedback && isSel && !isCorrect ? "border-[var(--ha-error)] bg-[var(--ha-error)]/10" :
                          "border-[var(--ha-border)] hover:bg-[var(--ha-bg)]"
                        }`}
                      >
                        <span className={`h-6 w-6 rounded-full border grid place-items-center text-xs font-bold shrink-0 ${isSel ? "border-[var(--ha-primary)] text-[var(--ha-primary)] bg-white" : "border-[var(--ha-border)] text-[var(--ha-muted)]"}`}>{o.key}</span>
                        <span className="text-sm text-[var(--ha-text)] leading-relaxed">{o.text}</span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <div className="mt-8 flex items-center justify-between">
        <span className="text-sm text-[var(--ha-muted)]">{Object.keys(answers).length}/{questions.length}</span>
        <Button data-testid={`gq-${quizId}-continue`} onClick={submit} disabled={!allAnswered || saving} className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-6">
          Continuar <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={1.75} />
        </Button>
      </div>
    </div>
  );
}
